import { PoseData, GestureEvent, GestureType } from '../../../types';
import { isAbove, isVisible } from '../../../utils/geometry';

/**
 * ArmsRaisedDetector detects when both arms are raised above shoulders.
 * 
 * Detection Logic:
 * - Both wrists must be above both shoulders (y coordinate comparison)
 * - Both elbows must be above both shoulders
 * - All required landmarks must have visibility > 0.5
 * 
 * Confidence Calculation:
 * - Based on distance above shoulders (normalized coordinates)
 * - 0.2 units above shoulders = full confidence (1.0)
 * - Confidence is average of wrist and elbow confidence scores
 * 
 * Validates Requirements 3.3.1
 */
export class ArmsRaisedDetector {
  /**
   * Detect if both arms are raised above shoulders.
   * 
   * @param pose Current pose data with landmark positions
   * @param timestamp Current timestamp in milliseconds
   * @returns GestureEvent if both arms raised, null otherwise
   */
  detect(pose: PoseData, timestamp: number): GestureEvent | null {
    if (!pose.detected) return null;
    
    const { leftWrist, rightWrist, leftElbow, rightElbow, leftShoulder, rightShoulder } = pose.landmarks;
    
    // Check if all required landmarks are visible
    if (!isVisible(leftWrist) || !isVisible(rightWrist) ||
        !isVisible(leftElbow) || !isVisible(rightElbow) ||
        !isVisible(leftShoulder) || !isVisible(rightShoulder)) {
      return null;
    }
    
    // Check if both wrists are above both shoulders
    const leftWristAboveLeftShoulder = isAbove(leftWrist, leftShoulder);
    const leftWristAboveRightShoulder = isAbove(leftWrist, rightShoulder);
    const rightWristAboveLeftShoulder = isAbove(rightWrist, leftShoulder);
    const rightWristAboveRightShoulder = isAbove(rightWrist, rightShoulder);
    
    const wristsAboveShoulders = leftWristAboveLeftShoulder && 
                                  leftWristAboveRightShoulder &&
                                  rightWristAboveLeftShoulder && 
                                  rightWristAboveRightShoulder;
    
    // Check if both elbows are above both shoulders
    const leftElbowAboveLeftShoulder = isAbove(leftElbow, leftShoulder);
    const leftElbowAboveRightShoulder = isAbove(leftElbow, rightShoulder);
    const rightElbowAboveLeftShoulder = isAbove(rightElbow, leftShoulder);
    const rightElbowAboveRightShoulder = isAbove(rightElbow, rightShoulder);
    
    const elbowsAboveShoulders = leftElbowAboveLeftShoulder && 
                                  leftElbowAboveRightShoulder &&
                                  rightElbowAboveLeftShoulder && 
                                  rightElbowAboveRightShoulder;
    
    if (!wristsAboveShoulders || !elbowsAboveShoulders) {
      return null;
    }
    
    // Calculate confidence based on how far above shoulders
    const avgShoulderY = (leftShoulder.y + rightShoulder.y) / 2;
    const avgWristY = (leftWrist.y + rightWrist.y) / 2;
    const avgElbowY = (leftElbow.y + rightElbow.y) / 2;
    
    // Distance above shoulders (in normalized coordinates)
    const wristDistance = avgShoulderY - avgWristY;
    const elbowDistance = avgShoulderY - avgElbowY;
    
    // Confidence increases with distance above shoulders
    // Assume 0.2 units above shoulders = full confidence
    const wristConfidence = Math.min(1.0, wristDistance / 0.2);
    const elbowConfidence = Math.min(1.0, elbowDistance / 0.2);
    
    // Overall confidence is average of wrist and elbow confidence
    const confidence = Math.max(0, Math.min(1, (wristConfidence + elbowConfidence) / 2));
    
    return {
      type: GestureType.ARMS_RAISED,
      confidence,
      timestamp
    };
  }
}
